import { reducerWithInitialState } from 'typescript-fsa-reducers';
import {
    setPartsSelectAction,
    newDesignAction,
    partsSelectActions,
    setIdAction,
    setBaseIdAction,
    fetchInitProductSetItemAction,
} from '@root/app/react/action';
import { setFamilycrestOk } from '@app/react/action/PartsSelectAction';
import { fetchCartItemAction } from '@app/react/action/fetchCartItem';
import type {
    PartsProduct,
    DesignSet,
} from '@app/react/const/type';
import { RecommendDesignItemsThreadPositionEnum as TPE } from '@app/react/const/type';
import { MAKIWARA_THREAD_MODE_B, MAKIWARA_THREAD_MODE_C } from '@app/react/const/variables';

export interface IinitState extends DesignSet {
    carvingOk: boolean;
    familycrestOk: boolean;
}

export const initStatePartsSelect: IinitState = {
    id: null,
    baseId: null,
    shaft: null,
    feather: null,
    thread: null,
    threadGloss: true,
    threadMode: MAKIWARA_THREAD_MODE_C,
    kebiki: null,
    nock: null,
    arrowhead: null,
    arrowlengthAdjust: null,
    arrowlength: null,
    unit: null,
    arrowinsert: null,
    twine: null,
    lame: null,
    carvingOk: false,
    carving: null,
    carvingString: null,
    familycrestOk: false,
    familycrest: null,
    familycrestColor: null,
    typezero: null,
    decoration: null,
    featherinside: null,
};

export const partsSelectReducer = reducerWithInitialState(initStatePartsSelect)
    .case(newDesignAction, (state, action) => {
        const { threadPosition, ...item } = action.item;

        const threadMode = threadPosition === TPE.Uraari
            ? MAKIWARA_THREAD_MODE_C
            : MAKIWARA_THREAD_MODE_B;

        return {
            ...item,
            threadMode,
            arrowlengthAdjust: null,
            carvingOk: false,
            familycrestOk: false,
        };
    })
    .case(setPartsSelectAction.done, (state, action) => {
        const { threadPosition, ...item } = action.params.item;

        const threadMode = threadPosition === TPE.Uraari
            ? MAKIWARA_THREAD_MODE_C
            : MAKIWARA_THREAD_MODE_B;

        return {
            ...state,
            ...item,
            threadMode,
            arrowlengthAdjust: item.arrowlength !== null,
            carvingOk: item.carving !== null,
            familycrestOk: item.familycrest !== null,
        };
    })
    .case(setIdAction, (state, action) => {
        if (state.id === null) {
            return {
                ...state,
                id: action.id,
            };
        }
        return {
            ...state,
        };
    })
    .case(setBaseIdAction, (state, action) => ({
        ...state,
        baseId: action.baseId,
    }))
    .case(partsSelectActions.shaft.started, (state, action) => {
        const shaft = action.param;
        return {
            ...state,
            shaft,
        };
    })
    .case(partsSelectActions.confirmShaft.started, (state, action) => {
        const shaft = action.param;
        return {
            ...state,
            shaft,
            arrowhead: null,
            nock: null,
            arrowinsert: null,
        };
    })
    .case(partsSelectActions.feather.started, (state, action) => {
        const hane = action.param;
        return {
            ...state,
            feather: hane,
        };
    })
    .case(partsSelectActions.confirmFeather.started, (state, action) => {
        const hane = action.param;
        return {
            ...state,
            feather: hane,
            familycrest: null,
            familycrestColor: null,
            familycrestOk: false,
        };
    })
    .case(partsSelectActions.confirmFeatherWithMakiwara.started, (state, action) => {
        const hane = action.param;
        return {
            ...state,
            feather: hane,
            featherinside: null,
        };
    })
    .case(partsSelectActions.thread.started, (state, action) => {
        const thread = action.param;
        return {
            ...state,
            thread,
        };
    })
    .case(partsSelectActions.threadGloss, (state, action) => {
        const threadGloss = action.gloss;
        return {
            ...state,
            threadGloss,
        };
    })
    .case(partsSelectActions.threadMode, (state, action) => {
        const threadMode = action.mode;
        return {
            ...state,
            threadMode,
        };
    })
    .case(partsSelectActions.kebiki.started, (state, action) => {
        const kebiki = action.param;
        return {
            ...state,
            kebiki,
        };
    })
    .case(partsSelectActions.nock.started, (state, action) => {
        const nock = action.param;
        return {
            ...state,
            nock,
        };
    })
    .case(partsSelectActions.arrowhead.started, (state, action) => {
        const arrowhead = action.param;
        return {
            ...state,
            arrowhead,
        };
    })
    .case(partsSelectActions.arrowlength.started, (state, action) => {
        const { adjust, arrowlength } = action;
        return {
            ...state,
            arrowlengthAdjust: adjust,
            arrowlength,
        };
    })
    .case(partsSelectActions.unit.started, (state, action) => {
        const unit = action.param;
        return {
            ...state,
            unit,
        };
    })
    .case(partsSelectActions.arrowinsert.started, (state, action) => {
        const arrowinsert = action.param;
        return {
            ...state,
            arrowinsert,
        };
    })
    .case(partsSelectActions.twine.started, (state, action) => {
        const twine = action.param;
        return {
            ...state,
            twine,
        };
    })
    .case(partsSelectActions.lame.started, (state, action) => {
        const lame = action.param;
        return {
            ...state,
            lame,
        };
    })
    .case(partsSelectActions.carving.started, (state, action) => {
        const { carvingOk, carving, carvingString } = action;

        const checkChangeHalfWideWidth = (
            prevCarving: PartsProduct | null,
            nextCarving: PartsProduct | null,
        ) => {
            const a = prevCarving?.options.carvingWritingType;
            const b = nextCarving?.options.carvingWritingType;

            if (a === b) return false;

            return true;
        };

        const newCarvingString = checkChangeHalfWideWidth(
            state.carving,
            carving,
        )
            ? ''
            : carvingString;

        return {
            ...state,
            carvingOk,
            carving,
            carvingString: newCarvingString,
        };
    })
    .case(partsSelectActions.familycrest.started, (state, action) => {
        const familycrest = action.param;
        return {
            ...state,
            familycrest,
        };
    })
    .case(partsSelectActions.fafamilycrestColor.started, (state, action) => {
        const familycrestColor = action.param;
        return {
            ...state,
            familycrestColor,
        };
    })
    .case(setFamilycrestOk, (state, action) => ({
        ...state,
        familycrestOk: action.ok,
        familycrest: action.ok ? state.familycrest : null,
        familycrestColor: action.ok ? state.familycrestColor : null,
    }))
    .case(partsSelectActions.typezero.started, (state, action) => {
        const typezero = action.param;
        return {
            ...state,
            typezero,
        };
    })
    .case(partsSelectActions.decoration.started, (state, action) => {
        const decoration = action.param;
        return {
            ...state,
            decoration,
        };
    })
    .case(partsSelectActions.featherinside.started, (state, action) => {
        const featherinside = action.param;
        return {
            ...state,
            featherinside,
        };
    })
    .case(fetchInitProductSetItemAction.done, (state, { result }) => {
        const { rid, designSet } = result;

        const threadMode = designSet.threadPosition === TPE.Uraari
            ? MAKIWARA_THREAD_MODE_C
            : MAKIWARA_THREAD_MODE_B;

        return {
            ...state,
            ...designSet,
            id: rid,
            baseId: rid,
            threadMode,
            arrowlengthAdjust: designSet.arrowlength !== null,
            carvingOk: designSet.carving !== null,
            familycrestOk: designSet.familycrest !== null,
            familycrestColor: {
                id: designSet.familycrestColor?.id ?? 0,
                productImage: designSet.familycrestColor?.productImage ?? '',
                textureImage: designSet.familycrestColor?.textureImage ?? '',
                name: '',
                enable: true,
            },
        };
    })
    .case(fetchCartItemAction.done, (state, { result }) => {
        const { cartId, designSet } = result;

        const threadMode = designSet.threadPosition === TPE.Uraari
            ? MAKIWARA_THREAD_MODE_C
            : MAKIWARA_THREAD_MODE_B;

        return {
            ...state,
            ...designSet,
            id: cartId,
            baseId: cartId,
            threadMode,
            arrowlengthAdjust: designSet.arrowlength !== null,
            carvingOk: designSet.carving !== null,
            familycrestOk: designSet.familycrest !== null,
            familycrestColor: {
                id: designSet.familycrestColor?.id ?? 0,
                productImage: designSet.familycrestColor?.productImage ?? '',
                textureImage: designSet.familycrestColor?.textureImage ?? '',
                name: '',
                enable: true,
            },
        };
    });
